import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import { LocaleProvider, useT } from '@/i18n/provider';
import { SITE_LOCALE, HREFLANG_TAGS } from '@/lib/site-config';
import { useInViewAnimation } from '@/hooks/useInViewAnimation';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { JsonLd } from '@/components/JsonLd';
import { LandingHeader } from '@/components/LandingHeader';
import { HeroSection } from '@/components/HeroSection';
import { FeaturesSection } from '@/components/FeaturesSection';
import { IngestDemoSection } from '@/components/IngestDemoSection';
import { AIAgentSection } from '@/components/AIAgentSection';
import { AISearchSEOSection } from '@/components/AISearchSEOSection';
import { PipelineSection } from '@/components/PipelineSection';
import { FleetSection } from '@/components/FleetSection';
import { ProductTourSection } from '@/components/product-tour/ProductTourSection';
import { ProblemSection } from '@/components/ProblemSection';
import { PricingSection } from '@/components/PricingSection';
import { CTASection } from '@/components/CTASection';
import { SocialProofSection } from '@/components/SocialProofSection';
import { OtlpSection } from '@/components/OtlpSection';
import { LandingFooter } from '@/components/LandingFooter';
import { CookieBanner } from '@/components/CookieBanner';
import { ScrollToTop } from '@/components/ScrollToTop';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { SecurityPage } from '@/pages/SecurityPage';
import { CookiePolicyPage } from '@/pages/CookiePolicyPage';
import { ImprintPage } from '@/pages/ImprintPage';
import { ComparisonPage } from '@/pages/ComparisonPage';
import { CompareDuoPage } from '@/pages/CompareDuoPage';
import { BlogPage } from '@/pages/BlogPage';
import { BlogPostPage } from '@/pages/BlogPostPage';
import { ChangelogPage } from '@/pages/ChangelogPage';
import { ContactPage } from '@/pages/ContactPage';
import { CommunityPage } from '@/pages/CommunityPage';
import { AISearchPage } from '@/pages/AISearchPage';
import { FleetPage } from '@/pages/FleetPage';
import { ArchitecturePage } from '@/pages/ArchitecturePage';
import { StatusPage } from '@/pages/StatusPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

const DocsLayout = lazy(() => import('@/pages/docs/DocsLayout'));

function Landing() {
  useInViewAnimation();
  const t = useT();

  useDocumentMeta({
    title: t('meta.home.title'),
    description: t('meta.home.description'),
  });

  // FAQ JSON-LD targets natural-language search queries like
  // "logs doorzoeken met AI", "search logs with AI", "Logs durchsuchen mit KI",
  // "rechercher des logs avec l'IA". One Q&A per language matches each locale's
  // primary head-keyword.
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    mainEntity: [1, 2, 3, 4].map((i) => ({
      '@type': 'Question',
      name: t(`meta.faq.q${i}`),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(`meta.faq.a${i}`),
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <JsonLd data={faqJsonLd} />
      <LandingHeader />
      <main>
        <HeroSection />
        <IngestDemoSection />
        <AIAgentSection />
        <AISearchSEOSection />
        <ProductTourSection />
        <ProblemSection />
        <PipelineSection />
        <FeaturesSection />
        <FleetSection />
        <OtlpSection />
        <PricingSection />
        <SocialProofSection />
        <CTASection />
      </main>
      <LandingFooter />
    </div>
  );
}

function DocsLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
      <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <LocaleProvider>
        <ScrollToTop />
        <Routes>
          {/* Marketing — served on every locale domain at root paths */}
          <Route path="/" element={<Landing />} />
          <Route path="/ai-log-search" element={<AISearchPage />} />
          <Route path="/agents" element={<FleetPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/imprint" element={<ImprintPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/architecture" element={<ArchitecturePage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/compare" element={<ComparisonPage />} />
          <Route path="/compare/:slug" element={<CompareDuoPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* English-only sections — only mounted on the .io build.
              On ccTLDs these paths 301 to logpulse.io via _redirects. */}
          {SITE_LOCALE === 'en' && (
            <>
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route
                path="/docs/*"
                element={
                  <Suspense fallback={<DocsLoadingFallback />}>
                    <DocsLayout />
                  </Suspense>
                }
              />
            </>
          )}

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <CookieBanner />
      </LocaleProvider>
    </BrowserRouter>
  );
}
